import { useState, type ReactNode } from 'react';
import { ActionDialog, TextInput } from './ActionDialog';
import type { ActionState, AdjustmentInput, Statement, StatementLine, VoidInput } from './pb-types';
import { day, dollars } from './pb-words';

export interface VoidDialogProps extends ActionState {
  account: string;
  statement: Statement;
  onConfirm: (input: VoidInput) => void;
  onClose: () => void;
}

const cents = (n: number): number => Math.round(n * 100) / 100;

/** The next reissue's number: PGS-202610-0007 becomes -R1, -R1 becomes -R2. */
export function reissueNumber(number: string): string {
  const m = /^(.*)-R(\d+)$/.exec(number);
  return m ? `${m[1]}-R${Number(m[2]) + 1}` : `${number}-R1`;
}

/** The credit staff add to the reissue, once it has an amount and a reason. */
function creditOf(amount: string, why: string): AdjustmentInput[] {
  const n = Number(amount);
  if (!(n > 0) || !why.trim()) return [];
  return [{ tenantId: null, amount: -cents(n), reason: why.trim(), corrects: null }];
}

/** The reissue's total: the lines kept plus what is added. */
const totalOf = (lines: StatementLine[], drop: number[], add: AdjustmentInput[]): number =>
  cents(
    lines.filter((l) => !drop.includes(l.id)).reduce((t, l) => t + l.amount, 0) +
      add.reduce((t, a) => t + a.amount, 0),
  );

/**
 * Void and reissue (ADR-0036, section 5.2): only an issued, unpaid statement. The original becomes void and its
 * replacement is issued at once with the lines kept, less those dropped, plus a credit; same due day.
 */
export function VoidDialog(p: VoidDialogProps): ReactNode {
  const s = p.statement;
  const [drop, setDrop] = useState<number[]>([]);
  const [credit, setCredit] = useState({ amount: '', why: '' });
  const add = creditOf(credit.amount, credit.why);
  const next = reissueNumber(s.number ?? '');
  const toggle = (id: number) =>
    setDrop((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));
  return (
    <ActionDialog
      {...p}
      size="lg"
      danger
      title={`Void ${s.number ?? ''} and reissue it`}
      confirmLabel={`Void and issue ${next}`}
      ready={drop.length > 0 || add.length > 0}
      onConfirm={(reason) => p.onConfirm({ reason, drop, add })}
      consequence={<VoidConsequence s={s} next={next} total={totalOf(s.lines, drop, add)} />}
    >
      <DropLines lines={s.lines} drop={drop} onToggle={toggle} />
      <div className="pb-grid2">
        <TextInput
          label="Credit to add ($)"
          type="number"
          step="0.01"
          inputMode="decimal"
          value={credit.amount}
          onChange={(amount) => setCredit((c) => ({ ...c, amount }))}
        />
        <TextInput
          label="Why the credit"
          value={credit.why}
          onChange={(why) => setCredit((c) => ({ ...c, why }))}
          max={500}
        />
      </div>
    </ActionDialog>
  );
}

function VoidConsequence(p: { s: Statement; next: string; total: number }): ReactNode {
  return (
    <>
      <p>
        {p.s.number} ({dollars(p.s.total)}) becomes <strong>void</strong> and{' '}
        <strong>{p.next}</strong> is issued at once for{' '}
        <span className="pb-money">{dollars(p.total)}</span>, due {day(p.s.dueOn)} as the original.
      </p>
      <p>
        Accounting must void {p.s.number} in QuickBooks by hand and import {p.next}; the void is in
        their mail.
      </p>
    </>
  );
}

function DropLines(p: {
  lines: StatementLine[];
  drop: number[];
  onToggle: (id: number) => void;
}): ReactNode {
  return (
    <fieldset className="pb-choices">
      <legend>Leave lines out of the reissue</legend>
      {p.lines.map((l) => (
        <label key={l.id} className="pb-choice">
          <input
            type="checkbox"
            checked={p.drop.includes(l.id)}
            onChange={() => p.onToggle(l.id)}
          />
          <span>
            {l.companyName ?? 'Account'}: {dollars(l.amount)}
            <small>{l.description}</small>
          </span>
        </label>
      ))}
    </fieldset>
  );
}
